import { QueryClient } from '@tanstack/react-query';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router';
import { registerSW } from 'virtual:pwa-register';
import { App } from './App';
import { newVersionReady, setWorkerUpdater, watchForUpdates } from './lib/app-update';
import { ApiError, configureApi } from './lib/api';
import { startLanguageSync } from './i18n/sync';
import { useAuth } from './stores/auth';
import { useNetwork, watchNetwork } from './stores/network';
import './index.css';

configureApi({
  getToken: () => useAuth.getState().token,
  onUnauthorized: () => useAuth.getState().clear(),
  onReachability: (ok) => useNetwork.getState().markApi(ok),
});
watchNetwork();
startLanguageSync();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      gcTime: 24 * 60 * 60_000,
      networkMode: 'offlineFirst',
      retry: (count, err) => (err instanceof ApiError && !err.isTransient ? false : count < 2),
      refetchOnWindowFocus: true,
    },
  },
});

// Last known data is persisted so the app shell shows something useful offline.
const persister = createSyncStoragePersister({ storage: window.localStorage, key: 'rw:query-cache' });

setWorkerUpdater(registerSW({ immediate: true, onNeedRefresh: newVersionReady }));
watchForUpdates();

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{ persister, maxAge: 7 * 24 * 60 * 60_000 }}
      // Restored data renders instantly (offline-friendly) but is always refreshed after a reload.
      onSuccess={() => void queryClient.invalidateQueries()}
    >
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </PersistQueryClientProvider>
  </StrictMode>,
);
